import { useState, useEffect, useRef } from "react";
import { Form, message } from "antd";
import { Invoice, PaymentTerm } from "../../types/invoice";
import { Booking, Product } from "../../types/booking";
import { supabase } from "../../lib/supabase";
import { SupabaseClient } from "@supabase/supabase-js";
import { createInvoice, updateInvoice, finalizeInvoice } from "../../services/invoiceService";
import { generateInvoiceLinesFromPricing, repairInklaringskostenInvoiceLines, repairOutboundShipmentTypeLines } from "../../services/pricingCalculationService";
import { syncInklaringskostenInvoiceLines } from "../../services/inklaringskostenInvoiceSync";
import { sortInvoiceLinesByCostOverviewOrder } from "../../services/costOverviewDisplayOrderService";
import {
  COMMODITY_INSPECTION_FEE_EUR,
  getEffectiveCommodityInspection,
} from "../../utils/commodityInspectionUtils";
import { getPostExportLicenseFee } from "../../utils/postExportLicenseUtils";
import {
  FormValues,
  InvoiceLine,
  PricingComponent,
  ContainerSize,
  SuggestedTariff,
  TariffRow,
  TariffCalculationType,
  InvoiceFormProps,
  addSpecialFeesToLines,
  recalculateInterestAmount,
  rankAndFormatTariffs,
  getPaymentTermFromBooking,
  getTotalVolumeFromBooking,
  calculateChargeableWeight,
  getTransportTypeId,
  getTariffCalculationTypeForBooking,
  resolveFclContainerSizeForRanking,
  syncPerPalletLineQuantities,
  syncOutboundFeesForShipmentType,
  zeroImportInvoiceVat,
} from "./invoiceFormHelpers";

const typedSupabase = supabase as unknown as SupabaseClient;

const FULL_BOOKING_SELECT = `
  *,
  company_info:company(*, pricing_payment_term:pricing_payment_terms(*)),
  transport_type_info:booking_transport_types(*),
  booking_type_info:bookings_type(*),
  origin_port_info:booking_origin_port(*),
  destination_port_info:booking_destination_port(*),
  booking_incoterm_info:bookings_incoterm(*),
  container_info:containers(id, reference, etd, eta, container_size, origin_port:booking_origin_port(*), destination_port:booking_destination_port(*)),
  product_info:booking_product(*),
  booking_delivery_info:booking_delivery(*)
`;

const LIGHTWEIGHT_BOOKING_SELECT = `
  id, reference, status, company_id, export_clearance, pick_up_fee,
  origin_port, destination_port, booking_type,
  company_info:company(id, name),
  transport_type_info:booking_transport_types(id, name, abbreviation),
  booking_type_info:bookings_type(id, name),
  booking_delivery_info:booking_delivery(id),
  invoices:booking_sales_invoice(id, moneybird_invoice_id, moneybird_sync_status)
`;

export function useInvoiceForm(props: InvoiceFormProps) {
  const { onSuccess, onCancel, initialBookingId, editInvoice } = props;
  const [form] = Form.useForm();

  // ─── State ─────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [initialDataLoading, setInitialDataLoading] = useState(true);
  const [pricingComponents, setPricingComponents] = useState<PricingComponent[]>([]);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [formValues, setFormValues] = useState<FormValues>({
    currency: "EUR",
    payment_term: PaymentTerm.DAYS_30,
    custom_payment_terms: "",
    lines: [{ description: "", quantity: 1, unit_price: 0, vat_percentage: 0 }],
  });
  const [autogeneratingLines, setAutogeneratingLines] = useState(false);
  const [pricingInfoVisible, setPricingInfoVisible] = useState(false);
  const [containerSizes, setContainerSizes] = useState<ContainerSize[]>([]);
  const [tariffCalculationType, setTariffCalculationType] = useState<TariffCalculationType>("per_kg");
  const [tariffRow, setTariffRow] = useState<TariffRow>({
    unit_price: 0, vat_percentage: 0, use_manual_quantity: false,
  });
  const [suggestedTariffs, setSuggestedTariffs] = useState<SuggestedTariff[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [selectedTariffId, setSelectedTariffId] = useState<number | null>(null);
  const [autoSaving, setAutoSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [savingLineIndex, setSavingLineIndex] = useState<number | null>(null);
  const [formInitialized, setFormInitialized] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);

  // Draft tracking: once a draft is created, we store its id and the
  // full Invoice object so auto-save works seamlessly.
  const [draftInvoice, setDraftInvoice] = useState<Invoice | null>(editInvoice ?? null);
  const isEditMode = !!(editInvoice || draftInvoice);
  const activeInvoice = editInvoice || draftInvoice;

  // Smart-selected (or clicked) tariff must win over autogenerated Transport Cost.
  // Suggestions and line generation race; without this the Cost Overview can keep
  // a wrong rate while the tariff card already shows the correct selection.
  const pendingTariffPriceRef = useRef<number | null>(null);
  const pendingTariffCalculationTypeRef = useRef<TariffCalculationType | null>(null);
  const autogeneratingRef = useRef(false);
  // Keep a live booking pointer so async tariff apply (after select) does not toast
  // "No booking selected" from a stale selectedBooking closure.
  const selectedBookingRef = useRef<Booking | null>(selectedBooking);
  selectedBookingRef.current = selectedBooking;

  // ─── Small helpers ─────────────────────────────────────────────

  const isMatchingContainer = (tariffContainerSize: string | null) => {
    return (
      tariffCalculationType === "per_container" &&
      !!tariffRow.container_type_id &&
      containerSizes.find((c) => c.id === tariffRow.container_type_id)?.name === tariffContainerSize
    );
  };

  // ─── Data fetching ─────────────────────────────────────────────

  const fetchBookingsLightweight = async () => {
    try {
      let allBookings: Booking[] = [];
      let from = 0;
      const pageSize = 1000;
      let hasMore = true;
      while (hasMore) {
        const { data, error } = await typedSupabase
          .from("bookings")
          .select(LIGHTWEIGHT_BOOKING_SELECT)
          .order("created_at", { ascending: false })
          .range(from, from + pageSize - 1);
        if (error) throw error;
        if (data && data.length > 0) {
          allBookings = [...allBookings, ...data];
          from += pageSize;
          hasMore = data.length === pageSize;
        } else {
          hasMore = false;
        }
      }
      setBookings(allBookings);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      message.error("Failed to load bookings");
    }
  };

  const fetchFullBooking = async (bookingId: number): Promise<Booking | null> => {
    try {
      const { data, error } = await supabase
        .from("bookings")
        .select(FULL_BOOKING_SELECT)
        .eq("id", bookingId)
        .single();
      if (error) throw error;
      return data as Booking | null;
    } catch (error) {
      console.error("Error fetching full booking:", error);
      message.error("Failed to load booking details");
      return null;
    }
  };

  const fetchPricingComponents = async () => {
    try {
      const { data, error } = await typedSupabase.from("pricing_components").select("*").order("name");
      if (error) throw error;
      if (data) setPricingComponents(data);
    } catch (error) {
      console.error("Error fetching pricing components:", error);
    }
  };

  const fetchContainerSizes = async () => {
    try {
      const { data, error } = await typedSupabase.from("container_size").select("*").order("name");
      if (error) throw error;
      if (data) setContainerSizes(data);
    } catch (error) {
      console.error("Error fetching container sizes:", error);
    }
  };

  // ─── Line management ──────────────────────────────────────────

  /** Persist lines in calculator kostenoverzicht order (China → freight → customs → outbound). */
  const applySortedLines = async (
    lines: InvoiceLine[],
    opts?: { autoSave?: boolean }
  ) => {
    const sorted = await sortInvoiceLinesByCostOverviewOrder(
      lines,
      pricingComponents
    );
    const normalized = zeroImportInvoiceVat(sorted);
    form.setFieldValue("lines", normalized);
    const newFormValues = { ...formValues, lines: normalized };
    setFormValues(newFormValues);
    if (opts?.autoSave !== false && isEditMode && activeInvoice) {
      autoSaveInvoice(newFormValues);
    }
    return normalized;
  };

  const addLineAndRemoveEmpty = (newLine: InvoiceLine) => {
    const lines = form.getFieldValue("lines") || [];
    const filtered = lines.filter(
      (l: InvoiceLine) => !(l.description === "" && l.unit_price === 0)
    );
    void applySortedLines([...filtered, newLine]);
  };

  const addOrReplaceLine = (newLine: InvoiceLine) => {
    const lines: InvoiceLine[] = form.getFieldValue("lines") || [];
    const filtered = lines.filter(
      (l) => !(l.description === "" && l.unit_price === 0)
    );
    const existingIdx = newLine.cost_type
      ? filtered.findIndex((l) => l.cost_type === newLine.cost_type)
      : -1;

    let updatedLines: InvoiceLine[];
    if (existingIdx >= 0) {
      updatedLines = [...filtered];
      updatedLines[existingIdx] = newLine;
    } else {
      updatedLines = [...filtered, newLine];
    }
    void applySortedLines(updatedLines);
  };

  const addCustomsToInvoiceLines = (newLines: Array<{
    description: string; quantity: number; unit_price: number;
    vat_percentage: number; cost_type: string;
  }>) => {
    let lines: InvoiceLine[] = form.getFieldValue("lines") || [];
    lines = lines.filter((l) => !(l.description === "" && l.unit_price === 0));

    const incomingTypes = new Set(newLines.map((l) => l.cost_type));
    // Keep a single combined voorschotprovisie in sync; drop legacy split lines too
    if (
      incomingTypes.has("customs_btw") ||
      incomingTypes.has("customs_duties") ||
      incomingTypes.has("customs_commission")
    ) {
      lines = lines.filter(
        (l) =>
          l.cost_type !== "customs_commission" &&
          l.cost_type !== "customs_commission_btw" &&
          l.cost_type !== "customs_commission_duties"
      );
    }

    for (const line of newLines) {
      const invoiceLine: InvoiceLine = {
        description: line.description, quantity: line.quantity,
        unit_price: Math.round(line.unit_price * 100) / 100,
        vat_percentage: line.vat_percentage ?? 0, cost_type: line.cost_type,
        manual: true,
      };
      const idx = invoiceLine.cost_type
        ? lines.findIndex((l) => l.cost_type === invoiceLine.cost_type)
        : -1;
      if (idx >= 0) {
        lines = [...lines];
        lines[idx] = invoiceLine;
      } else {
        lines = [...lines, invoiceLine];
      }
    }

    // Keep blanket voorschotsprovisie (if present) in sync with new line totals
    lines = recalculateInterestAmount(lines);
    void applySortedLines(lines);
  };

  /** Add/replace LMD lines in one write so base/diesel/tol are not lost to races. */
  const addLMDToInvoiceLines = async (
    newLines: Array<{
      description: string;
      quantity: number;
      unit_price: number;
      vat_percentage: number;
      cost_type: string;
    }>
  ) => {
    let lines: InvoiceLine[] = form.getFieldValue("lines") || [];
    lines = lines.filter((l) => !(l.description === "" && l.unit_price === 0));

    // Drop previous europallet line when re-adding LMD (re-added only if present in newLines).
    const incomingTypes = new Set(newLines.map((l) => l.cost_type));
    if (!incomingTypes.has("europallet_non_exchange")) {
      lines = lines.filter((l) => l.cost_type !== "europallet_non_exchange");
    }

    for (const line of newLines) {
      const invoiceLine: InvoiceLine = {
        description: line.description,
        quantity: line.quantity,
        unit_price: Math.round(line.unit_price * 100) / 100,
        vat_percentage: 0,
        cost_type: line.cost_type,
        manual: true,
      };
      const existingIdx = invoiceLine.cost_type
        ? lines.findIndex((l) => l.cost_type === invoiceLine.cost_type)
        : -1;
      if (existingIdx >= 0) {
        lines = [...lines];
        lines[existingIdx] = invoiceLine;
      } else {
        lines = [...lines, invoiceLine];
      }
    }

    await applySortedLines(lines);
  };

  /** Keep per-pallet fees (e.g. Outbound cost per pallet) in sync with LMD pallet count. */
  const syncOutboundPalletQuantities = (palletCount: number) => {
    void syncOutboundShipmentFees({ shipmentType: "pallet", cartonCount: 0, palletCount });
  };

  /**
   * Align outbound/administration fees with carton vs pallet mode.
   * Uses Sendcloud label count when provided (parcel = cartons, pallet carrier = pallets).
   */
  const syncOutboundShipmentFees = async (params: {
    shipmentType: "carton" | "pallet";
    cartonCount: number;
    palletCount: number;
  }) => {
    if (!selectedBooking) return;

    let lines: InvoiceLine[] = form.getFieldValue("lines") || [];

    const repaired = await repairOutboundShipmentTypeLines(
      selectedBooking,
      lines,
      params.shipmentType
    );
    lines = repaired.lines;

    const { lines: updated, changed: syncedChanged } = syncOutboundFeesForShipmentType(
      lines,
      { ...params, pricingComponents }
    );
    const normalized = zeroImportInvoiceVat(updated);
    const vatChanged = normalized.some(
      (line, index) => line.vat_percentage !== updated[index]?.vat_percentage
    );

    if (!repaired.changed && !syncedChanged && !vatChanged) return;

    await applySortedLines(normalized);
  };

  const addTariffToInvoiceLines = async (
    priceOverride?: number,
    calculationTypeOverride?: TariffCalculationType,
    bookingOverride?: Booking | null
  ) => {
    const booking = bookingOverride ?? selectedBookingRef.current;
    if (!booking) {
      // Avoid noisy toast during booking-select races (state not flushed yet).
      if (!form.getFieldValue("booking_id")) {
        message.error("No booking selected");
      }
      return;
    }
    const unitPrice = priceOverride ?? tariffRow.unit_price;
    if (unitPrice === 0) return;

    // Prefer explicit override (smart-match / deferred apply) so we never use the
    // default per_kg while React state for FCL/LCL has not flushed yet.
    // Manual Apply uses the Transport Type dropdown (tariffCalculationType).
    const calculationType =
      calculationTypeOverride ??
      pendingTariffCalculationTypeRef.current ??
      tariffCalculationType;

    pendingTariffPriceRef.current = unitPrice;
    pendingTariffCalculationTypeRef.current = calculationType;
    setTariffCalculationType(calculationType);
    setTariffRow((prev) => ({ ...prev, unit_price: unitPrice }));

    // Autogenerate will overwrite lines if still running — defer apply until it finishes.
    if (autogeneratingRef.current) return;

    try {
      setLoading(true);
      let quantity = 1;
      let description = "Shipment Fee (Tariff)";

      if (tariffRow.use_manual_quantity && tariffRow.manual_quantity !== undefined) {
        quantity = tariffRow.manual_quantity;
        if (calculationType === "per_kg") {
          description = `Air Freight Tariff (${quantity.toFixed(2)} kg - Manual)`;
        } else if (calculationType === "per_cbm") {
          description = `Sea Freight Tariff (${quantity.toFixed(2)} m³ - Manual)`;
        } else {
          const name = containerSizes.find((s) => s.id === tariffRow.container_type_id)?.name || "Container";
          description = `Sea Freight Tariff (${quantity} ${name} - Manual)`;
        }
      } else {
        if (calculationType === "per_kg") {
          quantity = calculateChargeableWeight(booking).chargeableWeight;
          description = `Air Freight Tariff (${quantity.toFixed(2)} kg)`;
        } else if (calculationType === "per_cbm") {
          quantity = getTotalVolumeFromBooking(booking);
          description = `Sea Freight Tariff (${quantity.toFixed(2)} m³)`;
        } else {
          const name = containerSizes.find((s) => s.id === tariffRow.container_type_id)?.name || "Container";
          description = `Sea Freight Tariff (${name})`;
        }
      }

      const newTariffLine: InvoiceLine = {
        description, quantity, unit_price: unitPrice,
        vat_percentage: tariffRow.vat_percentage || 0, cost_type: "tariff",
        manual: true,
      };

      const currentLines: InvoiceLine[] = form.getFieldValue("lines") || [];
      const existingIdx = currentLines.findIndex(
        (l) => l.cost_type === "tariff" || l.cost_type === "transport"
      );

      if (existingIdx >= 0) {
        const updated = [...currentLines];
        updated[existingIdx] = newTariffLine;
        form.setFieldsValue({ lines: updated });
        setFormValues((prev) => ({ ...prev, lines: updated }));
      } else {
        addLineAndRemoveEmpty(newTariffLine);
      }

      pendingTariffPriceRef.current = null;
      pendingTariffCalculationTypeRef.current = null;
    } finally {
      setLoading(false);
    }
  };

  const updateAllLinesTo0VAT = () => {
    const lines = form.getFieldValue("lines") || [];
    const updated = zeroImportInvoiceVat(lines).map((line: InvoiceLine) => ({
      ...line,
      cost_type: line.cost_type === "pricing_component" && line.id && line.id > 0
        ? line.id.toString() : line.cost_type,
    }));
    form.setFieldValue("lines", updated);
    setFormValues((prev) => ({ ...prev, lines: updated }));
  };

  // ─── Auto-generate invoice lines ──────────────────────────────

  const autogenerateInvoiceLines = async (booking: Booking) => {
    try {
      pendingTariffPriceRef.current = null;
      pendingTariffCalculationTypeRef.current = null;
      autogeneratingRef.current = true;
      setAutogeneratingLines(true);
      const emptyLines = [{ description: "", quantity: 1, unit_price: 0, vat_percentage: 0 }];
      form.setFieldsValue({ lines: emptyLines });
      setFormValues((prev) => ({ ...prev, lines: emptyLines }));

      const { lines, currency, error, templateSelectionInfo } =
        await generateInvoiceLinesFromPricing(booking, booking.company_id || undefined);
      if (error) throw error;

      const linesWithFees = addSpecialFeesToLines(booking, lines);

      if (linesWithFees.length > 0) {
        const linesWithVAT = linesWithFees.map((l) => ({ ...l, vat_percentage: 0 }));
        await applySortedLines(linesWithVAT, { autoSave: false });
        if (currency) {
          form.setFieldsValue({ currency });
          setFormValues((prev) => ({ ...prev, currency }));
        }
        if (!linesWithFees.some((l) => l.cost_type === "tariff" || l.cost_type === "transport")) {
          message.warning("No tariff found. You can add a manual tariff line item.");
        }
      } else {
        message.info("No pricing templates matched this booking. Add lines manually.");
      }
    } catch (error) {
      console.error("Error generating invoice lines:", error);
      message.error("Failed to generate invoice lines from pricing");
    } finally {
      autogeneratingRef.current = false;
      setAutogeneratingLines(false);
      // Re-apply smart-selected / clicked tariff that finished during generation
      const pendingPrice = pendingTariffPriceRef.current;
      const pendingType = pendingTariffCalculationTypeRef.current
        ?? getTariffCalculationTypeForBooking(booking);
      if (pendingPrice != null && pendingPrice !== 0) {
        await addTariffToInvoiceLines(pendingPrice, pendingType, booking);
      }
    }
  };

  // ─── Booking selection ─────────────────────────────────────────

  const handleBookingChange = async (value: number | null) => {
    if (value == null) {
      selectedBookingRef.current = null;
      setSelectedBooking(null);
      setSuggestedTariffs([]);
      setSelectedTariffId(null);
      setTariffRow({ unit_price: 0, vat_percentage: 0, use_manual_quantity: false });
      const emptyLines: InvoiceLine[] = [{ description: "", quantity: 1, unit_price: 0, vat_percentage: 0 }];
      form.setFieldsValue({ booking_id: undefined, lines: emptyLines });
      setFormValues((prev) => ({ ...prev, booking_id: undefined, lines: emptyLines }));
      return;
    }

    const lightBooking = bookings.find((b) => b.id === value);

    if (lightBooking?.invoices && Array.isArray(lightBooking.invoices) && lightBooking.invoices.length > 0) {
      const ids = lightBooking.invoices.map((inv) => inv.moneybird_invoice_id || `Invoice #${inv.id}`).join(", ");
      message.warning(`This booking already has ${lightBooking.invoices.length} invoice(s): ${ids}. Creating another may result in duplicates.`);
    }

    const fullBooking = await fetchFullBooking(value);
    if (!fullBooking) return;
    selectedBookingRef.current = fullBooking;
    setSelectedBooking(fullBooking);

    if (!isEditMode) {
      const emptyLines: InvoiceLine[] = [{ description: "", quantity: 1, unit_price: 0, vat_percentage: 0 }];
      form.setFieldsValue({ booking_id: value, lines: emptyLines });
      setFormValues((prev) => ({ ...prev, booking_id: value, lines: emptyLines }));
    } else {
      form.setFieldsValue({ booking_id: value });
      setFormValues((prev) => ({ ...prev, booking_id: value }));
    }

    const { paymentTermValue, customPaymentTerms } = getPaymentTermFromBooking(fullBooking);
    form.setFieldsValue({ payment_term: paymentTermValue, custom_payment_terms: customPaymentTerms });
    setFormValues((prev) => ({ ...prev, payment_term: paymentTermValue, custom_payment_terms: customPaymentTerms }));

    if (!isEditMode) {
      autogenerateInvoiceLines(fullBooking);
    } else {
      const currentLines = form.getFieldValue("lines") || [];
      const hasExisting = currentLines.some((l: InvoiceLine) => l.id && l.id > 0);
      if (!hasExisting) {
        const withFees = addSpecialFeesToLines(fullBooking, currentLines);
        if (withFees.length !== currentLines.length) {
          form.setFieldsValue({ lines: withFees });
          setFormValues((prev) => ({ ...prev, lines: withFees }));
        }
      }
    }
  };

  // ─── Form value changes ───────────────────────────────────────

  const handleValuesChange = (_changedValues: unknown, allValues: FormValues) => {
    if (allValues.lines) {
      let updated = recalculateInterestAmount(allValues.lines);
      updated = zeroImportInvoiceVat(updated);
      const origInterest = allValues.lines.find((l) => l.cost_type === "payment_interest");
      const newInterest = updated.find((l) => l.cost_type === "payment_interest");
      if (
        origInterest &&
        newInterest &&
        origInterest.unit_price !== newInterest.unit_price
      ) {
        form.setFieldsValue({ lines: updated });
      }
      allValues.lines = updated;
    }
    setFormValues(allValues);
  };

  // ─── Submit ────────────────────────────────────────────────────

  const resolveCompanyId = (booking: Booking | null): number | undefined => {
    if (!booking) return undefined;
    if (typeof booking.company_id === "number") return booking.company_id;
    if (!booking.company_info) return undefined;
    return Array.isArray(booking.company_info)
      ? booking.company_info[0]?.id
      : booking.company_info.id;
  };

  /** Always prefer selectedBooking — formValues.booking_id can be missing on draft create. */
  const buildInvoiceData = (values: FormValues): Partial<Invoice> => {
    const bookingId = selectedBooking?.id ?? values.booking_id ?? undefined;
    return {
      booking_id: bookingId as number,
      company_id: resolveCompanyId(selectedBooking) ?? undefined,
      currency: values.currency || "EUR",
      payment_term: values.payment_term,
      custom_payment_terms: values.custom_payment_terms,
      lines: (values.lines || []).map((l) => ({
        id: l.id, description: l.description,
        quantity: l.quantity || 0, unit_price: l.unit_price || 0,
        vat_percentage: l.vat_percentage ?? 0, cost_type: l.cost_type,
      })),
    };
  };

  const handleSubmit = async (values: FormValues) => {
    try {
      setLoading(true);
      const sortedLines = await sortInvoiceLinesByCostOverviewOrder(
        values.lines || [],
        pricingComponents
      );
      const invoiceData = buildInvoiceData({ ...values, lines: sortedLines });

      if (activeInvoice?.id) {
        // Finalize the draft or update the existing invoice
        invoiceData.status = "final";
        const { data, error } = await updateInvoice(activeInvoice.id, invoiceData);
        if (error) throw error;
        await finalizeInvoice(activeInvoice.id);
        message.success("Invoice created successfully");
        if (data) onSuccess(data);
      } else {
        invoiceData.status = "final";
        const { data, error } = await createInvoice(invoiceData as Invoice);
        if (error) throw error;
        message.success("Invoice created successfully");
        if (data) onSuccess(data);
      }
    } catch (error) {
      console.error("Error saving invoice:", error);
      message.error("Failed to save invoice");
    } finally {
      setLoading(false);
    }
  };

  // ─── Auto-save ─────────────────────────────────────────────────

  const autoSaveInvoice = async (values: FormValues) => {
    if (!activeInvoice?.id || !selectedBooking) return;
    try {
      setAutoSaving(true);
      const { error } = await updateInvoice(activeInvoice.id, buildInvoiceData(values));
      if (!error) setLastSaved(new Date());
    } catch (error) {
      console.error("Auto-save error:", error);
    } finally {
      setAutoSaving(false);
    }
  };

  const autoSaveInvoiceLine = async (lineIndex: number, lineData: InvoiceLine) => {
    if (!activeInvoice?.id || !selectedBooking) return;
    try {
      setSavingLineIndex(lineIndex);
      const currentLines = form.getFieldValue("lines") || [];
      currentLines[lineIndex] = lineData;
      const invoiceData = buildInvoiceData({ ...formValues, lines: currentLines });
      const { error } = await updateInvoice(activeInvoice.id, invoiceData);
      if (!error) setLastSaved(new Date());
    } catch (error) {
      console.error("Auto-save line error:", error);
    } finally {
      setSavingLineIndex(null);
    }
  };

  // ─── Draft creation ──────────────────────────────────────────────

  /**
   * Persist a draft as soon as the user leaves Tariff (step 1 → 2+).
   * Always writes booking_id + company_id from selectedBooking (not form state).
   */
  const createDraft = async (): Promise<boolean> => {
    if (activeInvoice?.id) return true;

    const booking = selectedBooking;
    if (!booking?.id) {
      message.error("Select a booking before continuing");
      return false;
    }

    const companyId = resolveCompanyId(booking);
    if (companyId == null) {
      message.error("Cannot save draft: booking has no company");
      return false;
    }

    try {
      setAutoSaving(true);

      form.setFieldsValue({ booking_id: booking.id });
      setFormValues((prev) => ({ ...prev, booking_id: booking.id }));

      const currentLines =
        (form.getFieldValue("lines") as InvoiceLine[] | undefined) ||
        formValues.lines ||
        [];

      const invoiceData: Invoice = {
        booking_id: booking.id,
        company_id: companyId,
        currency: formValues.currency || "EUR",
        payment_term: formValues.payment_term,
        custom_payment_terms: formValues.custom_payment_terms,
        status: "draft",
        lines: currentLines.map((l) => ({
          id: l.id,
          description: l.description,
          quantity: l.quantity || 0,
          unit_price: l.unit_price || 0,
          vat_percentage: l.vat_percentage ?? 0,
          cost_type: l.cost_type,
        })),
      };

      const { data, error } = await createInvoice(invoiceData);
      if (error) throw error;
      if (!data?.id) {
        message.error("Failed to save draft");
        return false;
      }

      setDraftInvoice(data as Invoice);
      setLastSaved(new Date());
      setFormInitialized(true);

      const lines = data.lines?.length
        ? zeroImportInvoiceVat(data.lines)
        : currentLines;
      form.setFieldsValue({ lines, booking_id: booking.id });
      setFormValues((prev) => ({
        ...prev,
        lines,
        booking_id: booking.id,
      }));
      return true;
    } catch (error) {
      console.error("Error creating draft invoice:", error);
      message.error("Failed to save draft");
      return false;
    } finally {
      setAutoSaving(false);
    }
  };

  // ─── Tariff suggestions ───────────────────────────────────────

  const fetchTariffSuggestions = async (
    calculationTypeForApply?: TariffCalculationType
  ) => {
    if (!selectedBooking) return;
    try {
      setLoadingSuggestions(true);
      setSelectedTariffId(null);

      const dateToMatch = (() => {
        const ci = selectedBooking.container_info;
        if (ci) {
          const c = Array.isArray(ci) ? ci[0] : ci;
          if (c?.etd) return c.etd;
        }
        return new Date().toISOString().split("T")[0];
      })();

      let originPort = selectedBooking.origin_port;
      let destinationPort = selectedBooking.destination_port;
      if (selectedBooking.container_info) {
        const c = Array.isArray(selectedBooking.container_info) ? selectedBooking.container_info[0] : selectedBooking.container_info;
        if (c?.origin_port) {
          const op = Array.isArray(c.origin_port) ? c.origin_port[0] : c.origin_port;
          if (op?.id) originPort = op.id;
        }
        if (c?.destination_port) {
          const dp = Array.isArray(c.destination_port) ? c.destination_port[0] : c.destination_port;
          if (dp?.id) destinationPort = dp.id;
        }
      }

      const transportTypeId = getTransportTypeId(selectedBooking);
      if (!transportTypeId || !originPort || !destinationPort) { setSuggestedTariffs([]); return; }

      const { data: matchingTariffs, error } = await typedSupabase
        .from("tariffs")
        .select("id, price, transport_type, origin_port, destination_port, bookings_type, container_size, tariff_bracket, from, to")
        .eq("transport_type", transportTypeId)
        .eq("origin_port", originPort)
        .eq("destination_port", destinationPort)
        .lte("from", dateToMatch)
        .or(`to.gte.${dateToMatch},to.is.null`)
        .order("price", { ascending: true });

      if (error || !matchingTariffs?.length) { setSuggestedTariffs([]); return; }

      const bracketIds = matchingTariffs.filter((t) => t.tariff_bracket).map((t) => t.tariff_bracket);
      let bracketInfo: Record<number, { id: number; from: number; to: number; transport_type: number | null }> = {};
      if (bracketIds.length > 0) {
        const { data } = await typedSupabase.from("tariff_brackets").select("id, from, to, transport_type").in("id", bracketIds);
        if (data) bracketInfo = Object.fromEntries(data.map((b) => [b.id, b]));
      }

      const btIds = [...new Set(matchingTariffs.filter((t) => t.bookings_type).map((t) => t.bookings_type))];
      const csIds = [...new Set(matchingTariffs.filter((t) => t.container_size).map((t) => t.container_size))];
      let bookingTypeInfo: Record<number, { id: number; name: string }> = {};
      let containerSizeInfo: Record<number, { id: number; name: string }> = {};
      if (btIds.length > 0) {
        const { data } = await typedSupabase.from("bookings_type").select("id, name").in("id", btIds);
        if (data) bookingTypeInfo = Object.fromEntries(data.map((b) => [b.id, b]));
      }
      if (csIds.length > 0) {
        const { data } = await typedSupabase.from("container_size").select("id, name").in("id", csIds);
        if (data) containerSizeInfo = Object.fromEntries(data.map((c) => [c.id, c]));
      }

      const bookingType = selectedBooking.booking_type_info
        ? Array.isArray(selectedBooking.booking_type_info) ? selectedBooking.booking_type_info[0]?.id : selectedBooking.booking_type_info.id
        : null;

      let containerSizeFromRecord: number | null = null;
      if (selectedBooking.container_id) {
        const { data: containerRow } = await typedSupabase
          .from("containers")
          .select("container_size")
          .eq("id", selectedBooking.container_id)
          .maybeSingle();
        containerSizeFromRecord = containerRow?.container_size ?? null;
      }

      const bookingVolume = getTotalVolumeFromBooking(selectedBooking);
      const bookingContainerSize = resolveFclContainerSizeForRanking(
        selectedBooking,
        containerSizeInfo,
        containerSizeFromRecord
      );

      const suggestions = rankAndFormatTariffs(matchingTariffs, bracketInfo, bookingTypeInfo, containerSizeInfo, {
        bookingType,
        bookingVolume,
        bookingContainerSize,
        transportTypeId,
      });

      setSuggestedTariffs(suggestions);
      if (suggestions.length > 0) {
        const best = suggestions[0];
        if (best.isSmartMatch || suggestions.length === 1) {
          // Prefer caller-provided type (booking select) over possibly-stale state.
          const calculationType =
            calculationTypeForApply ?? tariffCalculationType;
          setSelectedTariffId(best.id);
          setTariffCalculationType(calculationType);
          setTariffRow((prev) => ({
            ...prev,
            unit_price: best.price,
            container_type_id: best.containerSizeId ?? bookingContainerSize ?? prev.container_type_id,
          }));
          addTariffToInvoiceLines(best.price, calculationType);
        }
      }
    } catch (error) {
      console.error("Error fetching tariff suggestions:", error);
      setSuggestedTariffs([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // ─── Effects ───────────────────────────────────────────────────

  useEffect(() => {
    const init = async () => {
      setInitialDataLoading(true);
      try {
        const commonFetches = [fetchPricingComponents(), fetchContainerSizes()];
        if (initialBookingId) {
          const fullBooking = await fetchFullBooking(initialBookingId);
          if (fullBooking) { setBookings([fullBooking]); setSelectedBooking(fullBooking); }
          await Promise.all(commonFetches);
        } else {
          await Promise.all([fetchBookingsLightweight(), ...commonFetches]);
        }
      } finally {
        setInitialDataLoading(false);
      }
    };
    init();
  }, []);

  useEffect(() => {
    if (initialBookingId) {
      const refetch = async () => {
        setInitialDataLoading(true);
        try {
          const fullBooking = await fetchFullBooking(initialBookingId);
          if (fullBooking) { setBookings([fullBooking]); setSelectedBooking(fullBooking); }
        } finally { setInitialDataLoading(false); }
      };
      refetch();
    }
  }, [initialBookingId]);

  useEffect(() => {
    if (!isEditMode) {
      const timer = setTimeout(() => updateAllLinesTo0VAT(), 100);
      return () => clearTimeout(timer);
    }
  }, [isEditMode]);

  // Correct drafts/legacy lines that still carry VAT on import invoices
  useEffect(() => {
    const lines = formValues.lines;
    if (!lines?.some((line) => (line.vat_percentage ?? 0) !== 0)) return;
    const normalized = zeroImportInvoiceVat(lines);
    form.setFieldsValue({ lines: normalized });
    setFormValues((prev) => ({ ...prev, lines: normalized }));
  }, [formValues.lines, form]);

  useEffect(() => {
    const inv = editInvoice;
    if (!inv || bookings.length === 0) return;

    const initEdit = async () => {
      // Fetch the full booking so tariff/customs/LMD sections have all data
      const fullBooking = await fetchFullBooking(inv.booking_id);
      if (fullBooking) setSelectedBooking(fullBooking);

      let lines = inv.lines.map((l) => ({
        id: l.id, description: l.description, quantity: l.quantity,
        unit_price: l.unit_price, vat_percentage: l.vat_percentage ?? 0,
        cost_type: l.cost_type, template_source: l.template_source,
        calculation_type: l.calculation_type,
      }));

      // Fix legacy Inklaringskosten lines that billed base × HS count (e.g. 2×€45)
      if (fullBooking) {
        const repaired = await repairInklaringskostenInvoiceLines(fullBooking, lines);
        if (repaired.changed) {
          lines = repaired.lines;
          message.info("Inklaringskosten corrected (was charging base fee × HS codes)");
        }
      }

      lines = zeroImportInvoiceVat(lines);

      const editFormValues: FormValues = {
        booking_id: inv.booking_id, currency: inv.currency,
        payment_term: inv.payment_term || PaymentTerm.DAYS_30,
        custom_payment_terms: inv.custom_payment_terms || "",
        lines,
      };
      form.setFieldsValue(editFormValues);
      setFormValues(editFormValues);
      setFormInitialized(true);
    };
    initEdit();
  }, [editInvoice, bookings, form]);

  useEffect(() => {
    if (!isEditMode && initialBookingId && bookings.length > 0 && !initialDataLoading) {
      const booking = bookings.find((b) => b.id === initialBookingId);
      if (booking) {
        form.setFieldsValue({ booking_id: initialBookingId });
        setSelectedBooking(booking);
        const { paymentTermValue, customPaymentTerms } = getPaymentTermFromBooking(booking);
        form.setFieldsValue({ payment_term: paymentTermValue, custom_payment_terms: customPaymentTerms });
        setFormValues((prev) => ({
          ...prev,
          booking_id: initialBookingId,
          payment_term: paymentTermValue,
          custom_payment_terms: customPaymentTerms,
        }));
        autogenerateInvoiceLines(booking);
      }
    }
  }, [initialBookingId, bookings, form, isEditMode, initialDataLoading]);

  useEffect(() => {
    if (selectedBooking?.transport_type_info) {
      const nextType = getTariffCalculationTypeForBooking(selectedBooking);
      setTariffCalculationType(nextType);
      // Pass type explicitly — setState has not flushed when fetch's smart-match applies.
      fetchTariffSuggestions(nextType);
    }
  }, [selectedBooking]);

  useEffect(() => {
    if (selectedBooking && tariffCalculationType) fetchTariffSuggestions();
  }, [tariffCalculationType]);

  useEffect(() => {
    if (!activeInvoice?.id || !selectedBooking || !formInitialized) return;
    const id = setTimeout(() => autoSaveInvoice(formValues), 1000);
    return () => clearTimeout(id);
  }, [formValues, activeInvoice, selectedBooking, formInitialized]);

  // ─── Return ────────────────────────────────────────────────────

  return {
    form, loading, initialDataLoading, bookings, selectedBooking,
    formValues, setFormValues, pricingComponents, containerSizes,
    tariffCalculationType, setTariffCalculationType,
    tariffRow, setTariffRow, suggestedTariffs, setSuggestedTariffs,
    loadingSuggestions, selectedTariffId, setSelectedTariffId,
    autoSaving, lastSaved, savingLineIndex, formInitialized,
    pricingInfoVisible, setPricingInfoVisible,
    previewModalVisible, setPreviewModalVisible,
    autogeneratingLines, isEditMode,
    draftInvoice, activeInvoice, createDraft,
    handleBookingChange, handleValuesChange, handleSubmit,
    applyLinkedDeclaration: (declarationId: string) => {
      setSelectedBooking((prev) =>
        prev ? { ...prev, leancustoms_declaration_id: declarationId } : prev
      );
    },
    applyProductsHsUpdated: async (products: Product[]) => {
      const bookingWithProducts = selectedBooking
        ? ({ ...selectedBooking, product_info: products } as Booking)
        : null;
      if (!bookingWithProducts) return;

      setSelectedBooking(bookingWithProducts);

      const currentLines: InvoiceLine[] = form.getFieldValue("lines") || [];
      const { lines: synced, changed } = await syncInklaringskostenInvoiceLines(
        bookingWithProducts,
        currentLines
      );
      if (changed) {
        await applySortedLines(synced);
        message.success("Inklaringskosten updated for new HS codes");
      }
    },
    applyCompanyPaymentTerm: (
      term: {
        id: number;
        days: number;
        interest_vat_import_duties: number;
        created_at?: string;
      } | null,
    ) => {
      const booking = selectedBookingRef.current;
      if (!booking) return;

      const companyRaw = booking.company_info;
      const company = companyRaw
        ? Array.isArray(companyRaw)
          ? { ...companyRaw[0] }
          : { ...companyRaw }
        : null;
      if (!company) return;

      // Keep joined shape so getPaymentTermFromBooking / display see .days
      (company as { pricing_payment_term?: unknown }).pricing_payment_term =
        term ?? undefined;

      const nextBooking = {
        ...booking,
        company_info: Array.isArray(companyRaw) ? [company] : company,
      } as Booking;

      selectedBookingRef.current = nextBooking;
      setSelectedBooking(nextBooking);

      const { paymentTermValue, customPaymentTerms } =
        getPaymentTermFromBooking(nextBooking);
      form.setFieldsValue({
        payment_term: paymentTermValue,
        custom_payment_terms: customPaymentTerms,
      });
      setFormValues((prev) => {
        const updated = {
          ...prev,
          payment_term: paymentTermValue,
          custom_payment_terms: customPaymentTerms,
        };
        if (activeInvoice?.id) {
          void autoSaveInvoice(updated);
        }
        return updated;
      });
    },
    addTariffToInvoiceLines, addCustomsToInvoiceLines, addLMDToInvoiceLines,
    syncOutboundPalletQuantities, syncOutboundShipmentFees,
    autogenerateInvoiceLines, autoSaveInvoiceLine, autoSaveInvoice,
    fetchTariffSuggestions, isMatchingContainer,
    onSuccess, onCancel, editInvoice, initialBookingId,
  };
}
